import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Form, Input, Button, Upload, Typography, Flex, Table, Tag, Card, Spin, Radio } from "antd"
import DataTableResponsive from "ui/DataTableResponsive"
import PdfViewer from "ui/PdfViewer"
import { CheckOutlined, DeleteOutlined, InfoCircleOutlined, MoreOutlined } from "@ant-design/icons"
import styles from "./DocumentAdded.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import ExperienceSelector from "./ExperienceSelector"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { UploadIcon } from "ui/Icon/DSIcon/UploadIcon"
import DiscardConfirm from "ui/DiscardModal"
import { File as FileIcon } from "ui/Icon/DSIcon/File"
import { getTripList } from "store/trips"
import { createDigitalWaiver, updateDigitalWaiver } from "store/digital-waiver"
import { toaster } from "components/common/Toast"
import { useDevice } from "hooks/useDevice"
import { ResponsivePage } from "ui/ResponsivePage"
import { Modal } from "ui/Modal"
import CustomDrawer from "ui/Drawer"

const { Text } = Typography
const { Title } = Typography
export const DocumentsAddedModal = ({
  setIsLoading,
  isLoading,
  visible,
  onCancel,
  onSave,
  initialData,
  isView,
  useDrawerOnMobile,
  defaultExperienceIds = []
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isMobile, isTablet } = useDevice()
  const isCompact = isMobile || isTablet
  const [form] = Form.useForm()
  const [selectedExperiences, setSelectedExperiences] = useState([]) // experience ids
  const [fileList, setFileList] = useState([])
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [tripsState, setTripsState] = useState([])
  const [initialValues, setInitialValues] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  // When true, every guest on the booking must sign; when false, only the primary guest signs
  const [allGuestsMustSign, setAllGuestsMustSign] = useState(false)
  const { loading } = useSelector((state) => state?.digitalWaiver)
  // Set initial values when modal opens or initialData changes
  useEffect(() => {
    if (visible) {
      if (initialData) {
        // Extract trip IDs from assigned_trips objects
        const tripIds = initialData.assigned_trips?.map((trip) => String(trip.id)) || []
        // Set form fields for editing
        form.setFieldsValue({
          documentName: initialData.name,
          experiences: tripIds
        })

        // Set selected experiences
        setSelectedExperiences(tripIds)
        // Hydrate signature-requirement radios from backend sign_by ("all" | "primary")
        setAllGuestsMustSign(initialData.sign_by === "all")
        // Set file list with existing document
        if (initialData.document_url) {
          const fileName = initialData.document_filename || initialData.document_url.split("/").pop() || "document.pdf"
          const now = new Date()

          // Initial file object with placeholder data
          const initialFileObj = {
            uid: `existing-${Date.now()}`,
            lastModified: now.getTime(),
            lastModifiedDate: now.toISOString(),
            name: fileName,
            type: "application/pdf",
            percent: 0,
            status: "uploading",
            url: initialData.document_url
          }

          setFileList([initialFileObj])
          // Fetch the actual document
          const fetchDocument = async () => {
            try {
              const response = await fetch(initialData.document_url)
              if (response.ok) {
                // Get the blob and create a file object
                const blob = await response.blob()
                const file = new File([blob], fileName, {
                  type: blob.type || "application/pdf",
                  lastModified: now.getTime()
                })

                // Create the final file object with all properties
                const fileObj = {
                  ...initialFileObj,
                  size: blob.size,
                  status: "done",
                  originFileObj: file
                }

                // Update file list with actual file data
                setFileList([fileObj])

                // Set form field with the actual file
                form.setFieldsValue({ documentFile: file })

                // Create and set object URL for preview
                const objectUrl = URL.createObjectURL(blob)
                setPdfUrl(objectUrl)
                setIsLoading(false)
                // Clean up function to revoke object URL on unmount
                return () => {
                  URL.revokeObjectURL(objectUrl)
                }
              }

              throw new Error(`HTTP error! status: ${response.status}`)
            } catch (error) {
              console.error("Error fetching document:", error)
              // Update file list with error state
              setFileList([
                {
                  ...initialFileObj,
                  status: "error",
                  error: "Failed to load document"
                }
              ])
            }
          }

          fetchDocument()
        }
      } else {
        // Reset form for new document, pre-attaching any experiences the caller passed in
        // (e.g. the experience being edited in the wizard)
        const defaultTripIds = defaultExperienceIds.map(String)
        form.resetFields()
        form.setFieldsValue({ experiences: defaultTripIds })
        setFileList([])
        setSelectedExperiences(defaultTripIds)
        setPdfUrl(null)
        setAllGuestsMustSign(false)
      }

      // Set initial values for change detection
      setInitialValues({
        documentName: initialData?.name || form.getFieldValue("documentName") || "",
        allGuestsMustSign: initialData?.sign_by === "all",
        experiences: initialData?.assigned_trips?.map((trip) => String(trip.id)) || form.getFieldValue("experiences") || [],
        fileList: initialData?.document_url
          ? [
              {
                uid: "-1",
                name: initialData.name || "document.pdf",
                status: "done",
                url: initialData.document_url
              }
            ]
          : [...fileList]
      })
    }
  }, [visible, initialData])
  console.log(pdfUrl, "pdfurlldfd")
  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialValues) return false

    const currentValues = {
      documentName: form.getFieldValue("documentName") || "",
      allGuestsMustSign,
      experiences: form.getFieldValue("experiences") || [],
      fileList: [...fileList]
    }

    return (
      initialValues.documentName !== currentValues.documentName ||
      initialValues.allGuestsMustSign !== currentValues.allGuestsMustSign ||
      JSON.stringify(initialValues.experiences) !== JSON.stringify(currentValues.experiences) ||
      initialValues.fileList.length !== currentValues.fileList.length
    )
  }

  const handleCancel = () => {
    if (!isView && hasUnsavedChanges()) {
      setShowDiscardConfirm(true)
    } else {
      onCancel()
      resetForm()
    }
  }

  const resetForm = () => {
    form.resetFields()
    setFileList([])
    setPdfUrl(null)
    setSelectedExperiences([])
    setAllGuestsMustSign(false)
  }

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false)
    onCancel()

    // Clear file list and PDF preview
    setFileList([])
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl)
      setPdfUrl(null)
    }

    form.resetFields()
  }

  const handleCancelDiscard = () => {
    setShowDiscardConfirm(false)
  }

  const handleUpload = ({ fileList: newFileList }) => {
    setIsUploading(true)
    setFileList(newFileList)
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj
      if (file) {
        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
        if (isPdf) {
          // Create a simple object URL for the file
          const url = URL.createObjectURL(file)
          setPdfUrl(url)
          form.setFields([{ name: "documentFile", value: file, errors: [] }])
        } else {
          form.setFields([{ name: "documentFile", value: file, errors: ["Only PDF files are acceptable."] }])
        }
      } else {
        form.setFields([{ name: "documentFile", value: newFileList[0]?.originFileObj, errors: [] }])
      }
    } else {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
        setPdfUrl(null)
      }
      // Clear any lingering error (e.g. corrupted/malformed) when the file is removed
      form.setFields([{ name: "documentFile", value: undefined, errors: [] }])
    }
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl])

  // Cleanup when modal closes
  useEffect(() => {
    if (!visible) {
      resetForm()
      setShowDiscardConfirm(false)
      setInitialValues(null)
      setErrorMessage("")
      setIsUploading(false)
    }
  }, [visible])

  const experiencesMemo = React.useMemo(
    () =>
      tripsState?.map((trip) => ({
        id: String(trip?.id),
        name: trip?.title || "Untitled",
        guests: trip?.guests || 0,
        price: trip?.price ? Number(trip?.price) : 0,
        status: trip?.status,
        currency: "$",
        duration: trip?.duration || "-",
        waiver_count: trip?.waiver_count ?? 0
      })),
    [tripsState]
  )
  // Derive selected experience data instead of storing separate state (prevents extra render)
  const selectedExperienceData = React.useMemo(
    () => (selectedExperiences.length ? experiencesMemo.filter((exp) => selectedExperiences.includes(String(exp.id))) : []),
    [selectedExperiences, experiencesMemo]
  )
  // Guide category to boats state
  useEffect(() => {
    // Fetch trips on mount
    const fetchTrips = async () => {
      try {
        const response = await dispatch(getTripList())
        // The response should be an array of trips
        // if (Array.isArray(response)) {
        //   setTripsState(response)
        // }
        if (response && Array.isArray(response.data)) {
          setTripsState(response.data)
        }
      } catch (error) {
        console.error("Error fetching trips:", error)
        setTripsState([])
      }
    }

    fetchTrips()
  }, [dispatch])

  const onFinish = async (values) => {
    const hasDocumentToSubmit = fileList.length > 0 || Boolean(initialData?.document_url)
    if (!hasDocumentToSubmit) {
      form.setFields([
        {
          name: "documentFile",
          errors: ["Please upload a document"]
        }
      ])
      return
    }

    // Convert selected experience IDs to numbers for the API
    const tripIds = selectedExperiences.map(Number)

    try {
      const formData = new FormData()
      formData.append("digital_waiver[name]", values.documentName)
      // Who must sign: "all" (every guest on the booking) or "primary" (primary guest only)
      formData.append("digital_waiver[sign_by]", allGuestsMustSign ? "all" : "primary")

      // Only append file if it's a new upload or we're editing without an existing file
      if (fileList.length > 0 && fileList[0].originFileObj && isUploading) {
        formData.append("digital_waiver[document]", fileList[0].originFileObj)
      }
      if (initialData?.document_id && !isUploading) {
        formData.append("digital_waiver[document_id]", initialData.document_id)
      }

      // Add trip IDs to form data
      tripIds.forEach((id) => {
        formData.append("digital_waiver[trip_ids][]", id)
      })

      let result
      if (initialData?.id) {
        // Update existing document
        // formData.append("digital_waiver[id]", initialData.id)
        result = await dispatch(updateDigitalWaiver(initialData.id, formData))
      } else {
        // Create new document
        result = await dispatch(createDigitalWaiver(formData))
      }

      // Show success message
      setIsUploading(false)
      toaster.success(result.message || (initialData?.id ? "Document updated successfully" : "Document created successfully"))

      resetForm()

      // Call onSave with the result — onSave is responsible for closing/navigating
      if (onSave) {
        onSave(result)
      }
    } catch (error) {
      const errors = error?.data?.errors
      const mainMessage = error?.data?.message
      if (mainMessage) toaster.error(mainMessage)
      if (errors?.name?.length) {
        form.setFields([{ name: "documentName", errors: [errors.name[0]] }])
      }
      if (errors?.document?.length) {
        form.setFields([{ name: "documentFile", errors: [errors.document[0]] }])
      }
      if (errors?.trip_ids?.length) {
        setErrorMessage(errors.trip_ids[0])
      }
    }
  }
  const desktopColumns = [
    {
      title: "",
      align: "center",
      width: 50,
      render: (_value, _record, index) => <Text>{index + 1}</Text>
    },
    {
      title: "Experience",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (name) => (
        <Text className={`${styles.waiverLink} ellipsis block`} fw="500" title={name}>
          {name}
        </Text>
      )
    },
    {
      title: "Guests",
      dataIndex: "guests",
      key: "guests",
      align: "left",
      width: 100,
      render: (guests) => (
        <Text>
          {guests} {guests > 1 ? "guests" : "guest"}
        </Text>
      )
    },
    {
      title: "Price",
      key: "price",
      align: "right",
      width: 100,
      render: (_, record) => (
        <Text fw="400">
          {record.currency}
          {record.price.toFixed(2)}
        </Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      width: 110,
      render: (status) => {
        const isPublished = String(status).toLowerCase() === "published"
        const displayText = String(status).charAt(0).toUpperCase() + String(status).slice(1)
        return (
          <Tag className={styles.statusTag} color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
            {displayText}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 50,
      render: (_, record) => (
        <Button
          disabled={isView}
          type="text"
          icon={<DeleteOutlined className={styles.removeIcon} />}
          onClick={() => handleRemoveExperience(record.id)}
          className={styles.removeExperienceButton}
          aria-label="Remove experience"
        />
      )
    }
  ]

  const mobileExperienceData = React.useMemo(
    () =>
      selectedExperienceData.map((exp) => {
        const isPublished = String(exp.status).toLowerCase() === "published"
        const displayStatus = String(exp.status).charAt(0).toUpperCase() + String(exp.status).slice(1)
        return {
          id: exp.id,
          name: exp.name,
          guests: `${exp.guests} ${exp.guests === 1 ? "guest" : "guests"}`,
          price: `${exp.currency}${Number(exp.price).toFixed(2)}`,
          status: (
            <div className="flex-1 text-left">
              <Tag className={styles.statusTag} color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
                {displayStatus}
              </Tag>
            </div>
          )
        }
      }),
    [selectedExperienceData]
  )
  const handleRemoveExperience = (expId) => {
    setSelectedExperiences((prev) => {
      const next = prev.filter((id) => id !== expId)
      // Keep form field in sync so controlled value from Form doesn't push stale tag back
      form.setFieldsValue({ experiences: next })
      return next
    })
  }

  const getModalTitle = () => {
    if (isView) {
      return "View Digital Waivers & Contracts"
    }
    if (initialData?.id) {
      return "Edit Digital Waivers & Contracts"
    } else {
      return t("digital_waiver_and_contracts.add_digital_waiver_and_contracts")
    }
  }

  const modalTitle = getModalTitle()

  const modalContent = (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <Form form={form} layout="vertical" onFinish={onFinish} className={styles.form}>
        <Card className={`${styles.card} p-3xl pb-3 mb-m`}>
          <LabeledField
            labelKey={t("digital_waiver_and_contracts.document_name")}
            name="documentName"
            rules={[{ required: true, message: t("digital_waiver_and_contracts.required_document_name") }]}
            className={styles.formItem}
          >
            <Input disabled={isView} placeholder={t("digital_waiver_and_contracts.document_name_placeholder")} />
          </LabeledField>
          <div className={styles.uploadSection}>
            <Form.Item
              name="documentFile"
              rules={[{ required: true, message: t("digital_waiver_and_contracts.required_document_upload") }]}
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList}
              className={styles.formItem}
            >
              <Text className={styles.uploadLabel}>{t("digital_waiver_and_contracts.upload_document")}</Text>
              {fileList.length > 0 ? (
                <Flex align="center" justify="center" gap={16} className={`${styles.uploadedFileContainer}`}>
                  <Flex align="center" gap={2} className={isView ? styles.uploadedFileInfoDisabled : null}>
                    <FileIcon className={styles.fileIcon} />
                    <Text className={styles.uploadedFileName}>{fileList[0].name}</Text>
                  </Flex>
                  <Button
                    type="text"
                    danger
                    onClick={() => handleUpload({ fileList: [] })}
                    className={styles.removeButton}
                    disabled={isView}
                  >
                    Remove
                  </Button>
                </Flex>
              ) : (
                <Upload.Dragger
                  disabled={isView}
                  fileList={fileList}
                  onChange={handleUpload}
                  beforeUpload={(file) => {
                    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
                    if (isPdf) {
                      form.setFields([{ name: "documentFile", errors: [] }])
                    } else {
                      form.setFields([{ name: "documentFile", errors: ["Only PDF files are acceptable."] }])
                    }
                    return false
                  }}
                  multiple={false}
                  accept=".pdf"
                  className={styles.uploadArea}
                >
                  <div className={styles.uploadContent}>
                    <div className={styles.circle}>
                      <UploadIcon />
                    </div>
                    <div>
                      <Button type="link" className={styles.uploadButton}>
                        {t("digital_waiver_and_contracts.upload")}
                      </Button>
                    </div>
                    <Flex align="center" gap={4}>
                      <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                      <Text style={{ color: "#8c8c8c", fontSize: 13 }}>Only PDF files are acceptable.</Text>
                    </Flex>
                  </div>
                </Upload.Dragger>
              )}

              {pdfUrl && (
                <div className={styles.pdfPreviewContainer}>
                  <div className={`mb-xs ${styles.documentTitle}`}>Document Preview</div>
                  <PdfViewer url={pdfUrl} height={600} />
                </div>
              )}
            </Form.Item>
          </div>
          <div className={styles.signatureRequirementSection}>
            <Text className={styles.signatureRequirementLabel}>{t("digital_waiver_and_contracts.who_needs_to_sign")}</Text>
            <Radio.Group
              value={allGuestsMustSign}
              disabled={isView}
              onChange={(event) => setAllGuestsMustSign(event.target.value)}
              className="mt-xs"
            >
              <Flex vertical gap={12}>
                <Radio value={false}>{t("digital_waiver_and_contracts.primary_guest_only")}</Radio>
                <Radio value={true}>{t("digital_waiver_and_contracts.all_guests")}</Radio>
              </Flex>
            </Radio.Group>
            <Text className={styles.signatureRequirementHint}>{t("digital_waiver_and_contracts.all_guests_sign_hint")}</Text>
          </div>
        </Card>

        <Card className={`${styles.assignExperiencesSection} p-3xl`}>
          <Title level={5} className={`${styles.sectionTitle} mb-xl`}>
            {t("digital_waiver_and_contracts.assign_experiences")} <span className="ml-xxs">({selectedExperienceData.length})</span>
          </Title>
          <LabeledField
            name="experiences"
            labelKey={`${t("digital_waiver_and_contracts.experiences")}*`}
            className="mb-0"
            rules={[{ required: true, message: t("digital_waiver_and_contracts.experience_required") }]}
          >
            <ExperienceSelector
              experiences={experiencesMemo}
              value={selectedExperiences}
              isView={isView}
              onOpen={async () => {
                const response = await dispatch(getTripList())
                if (response && Array.isArray(response.data)) {
                  setTripsState(response.data)
                }
              }}
              onChange={(vals) => {
                setErrorMessage("")
                const strVals = vals.map(String)
                if (strVals.length !== selectedExperiences.length || strVals.some((value, index) => value !== selectedExperiences[index])) {
                  setSelectedExperiences(strVals)
                  form.setFieldsValue({ experiences: strVals })
                }
              }}
            />
            {selectedExperienceData.length > 0 && (
              <div>
                <Title level={5} className={`mt-5 ${styles.selectedExperiencesTitle}`}>
                  {t("digital_waiver_and_contracts.assigned_to_experiences")}
                </Title>
                {isCompact ? (
                  <DataTableResponsive
                    data={mobileExperienceData}
                    labelMap={{ name: "Experience", guests: "Guests", price: "Price", status: "Status" }}
                    fieldOrder={["name", "guests", "price", "status"]}
                    actionIcon={!isView ? <MoreOutlined /> : undefined}
                    getTableActions={
                      !isView
                        ? (record) => [
                            {
                              label: "Remove",
                              color: "danger",
                              variant: "solid",
                              onClick: () => handleRemoveExperience(record.id)
                            }
                          ]
                        : undefined
                    }
                  />
                ) : (
                  <Table
                    disabled={isView}
                    className={styles.table}
                    dataSource={selectedExperienceData}
                    rowKey="id"
                    pagination={false}
                    columns={desktopColumns}
                    scroll={{ y: 300 }}
                  />
                )}
              </div>
            )}
          </LabeledField>
          {errorMessage && (
            <Text type="danger" className={styles.errorMessage}>
              {errorMessage}
            </Text>
          )}
        </Card>

        <Flex align="center" gap={12} className={`${styles.publishButtonsContainer}`}>
          <Button onClick={handleCancel} color="primary" size="large" variant="outlined">
            {t("common.cancel") || "Cancel"}
          </Button>
          {!isView && (
            <Button loading={loading} disabled={loading} color="primary" size="large" htmlType="submit" variant="solid">
              {t("digital_waiver_and_contracts.save_and_publish") || "Save & Publish"}
            </Button>
          )}
        </Flex>
      </Form>
    </Spin>
  )

  // On mobile, the Experiences wizard wants a bottom drawer (useDrawerOnMobile),
  // while the standalone Digital Waiver page uses an inline full-page ResponsivePage.
  const renderMobileView = () => {
    if (useDrawerOnMobile) {
      return (
        <CustomDrawer open={visible} setOpen={() => handleCancel()} title={modalTitle}>
          {modalContent}
        </CustomDrawer>
      )
    }
    return (
      visible && (
        <ResponsivePage pageTitle={modalTitle} onBackClick={handleCancel}>
          {modalContent}
        </ResponsivePage>
      )
    )
  }

  return (
    <>
      {isMobile ? (
        renderMobileView()
      ) : (
        <Modal
          title={modalTitle}
          open={visible}
          onCancel={handleCancel}
          footer={null}
          width={800}
          maskClosable={false}
          className={styles.documentsModal}
        >
          {modalContent}
        </Modal>
      )}
      <DiscardConfirm
        open={showDiscardConfirm}
        onCancel={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        title="Discard Changes?"
        description={t("global.discard_changes")}
        t={t}
      />
    </>
  )
}

DocumentsAddedModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isView: PropTypes.bool,
  useDrawerOnMobile: PropTypes.bool,
  defaultExperienceIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    document_url: PropTypes.string,
    document_filename: PropTypes.string,
    document_id: PropTypes.number,
    sign_by: PropTypes.oneOf(["primary", "all"]),
    assigned_trips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number
      })
    ),
    trip_ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  })
}

DocumentsAddedModal.defaultProps = {
  initialData: null,
  useDrawerOnMobile: false
}
